import React, { useState, useEffect, useRef } from 'react';
import { ApiService } from '../services/api';
import { partnerWsClient } from '../services/partnerWs';
import { Venue, PartnerNotification } from '../types';
import { ConicGlowCard } from '../components/common/ConicGlowCard';
import {
  BellIcon,
  ShareIcon,
  BuildingIcon,
  CameraIcon,
  CalendarIcon,
  SparklesIcon,
  ShieldCheckIcon,
  GlobeIcon,
  InstagramIcon,
  FacebookIcon,
  YoutubeIcon,
  CheckIcon,
  RadioDotIcon
} from '../components/common/Icons';

export const PartnerPortalPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'notifications' | 'pricing' | 'availability' | 'photos' | 'social' | 'claim'>('notifications');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Auth Form State
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  // Venue & Portal State
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [notifications, setNotifications] = useState<PartnerNotification[]>([
    {
      id: 'notif-demo-1',
      venueId: 'v-karsaz-01',
      bookingId: 'MHF-882194',
      type: 'provisional_hold',
      title: 'New 72h Provisional Hold • #MHF-882194',
      message: 'Barat Reception for 400 Guests on 25 Nov 2026 (Dinner Shift). Estimated Quote: Rs. 740,000 PKR.',
      isRead: false,
      createdAt: new Date().toISOString(),
      customerContact: {
        name: 'Farhan Siddiqui',
        phone: '+92 300 8219940',
        email: 'farhan.siddiqui@intrepidtech.net'
      }
    }
  ]);
  const [unreadCount, setUnreadCount] = useState(1);

  // Hall Rental & Capacity Matrix State
  const [peakRental, setPeakRental] = useState<number>(330000);
  const [offPeakRental, setOffPeakRental] = useState<number>(240000);
  const [maxCapacityPax, setMaxCapacityPax] = useState<number>(800);
  const [valetCapacityCars, setValetCapacityCars] = useState<number>(250);
  const [packageSaveStatus, setPackageSaveStatus] = useState<string | null>(null);

  // Availability State
  const [availabilitySlots, setAvailabilitySlots] = useState<{ date: string; slot: 'lunch' | 'dinner'; status: string }[]>([
    { date: '2026-11-25', slot: 'lunch', status: 'available' },
    { date: '2026-11-25', slot: 'dinner', status: 'available' },
    { date: '2026-11-26', slot: 'lunch', status: 'available' },
    { date: '2026-11-26', slot: 'dinner', status: 'blocked_by_owner' },
    { date: '2026-11-27', slot: 'lunch', status: 'available' },
    { date: '2026-11-27', slot: 'dinner', status: 'available' },
    { date: '2026-11-28', slot: 'lunch', status: 'available' },
    { date: '2026-11-28', slot: 'dinner', status: 'available' }
  ]);

  // Reply Modal / State
  const [activeReplyBookingId, setActiveReplyBookingId] = useState<string | null>('MHF-882194');
  const [replyMessage, setReplyMessage] = useState('');
  const [counterQuotePkr, setCounterQuotePkr] = useState<string>('');
  const [replyStatus, setReplyStatus] = useState<string | null>(null);

  // Claim State
  const [claimPhone, setClaimPhone] = useState('');
  const [claimNotes, setClaimNotes] = useState('');
  const [claimStatus, setClaimStatus] = useState<string | null>(null);

  // Photo Curation & Upload State
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadCategory, setUploadCategory] = useState('hall');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [hiddenPhotos, setHiddenPhotos] = useState<Record<number, boolean>>({});

  // Social & Web Profile State
  const [instagramUrl, setInstagramUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('https://majesticbanquet.pk');
  const [whatsappNumber, setWhatsappNumber] = useState('+92 300 1234589');
  const [socialSaveStatus, setSocialSaveStatus] = useState<string | null>(null);

  // Check login on mount
  useEffect(() => {
    const token = ApiService.getAuthToken();
    if (token) {
      ApiService.partnerGetMe()
        .then((res) => {
          setUser(res.user);
          setIsLoggedIn(true);
        })
        .catch(() => {
          ApiService.setAuthToken(null);
          setIsLoggedIn(false);
        });
    }
  }, []);

  // Load venues on mount
  useEffect(() => {
    ApiService.searchVenues({ limit: 20 })
      .then((res) => {
        setVenues(res.venues);
        if (res.venues.length > 0) {
          setSelectedVenue(res.venues[0]);
        }
      })
      .catch((e) => console.error('Failed to load venues:', e));
  }, []);

  // Connect WebSocket when logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    const token = ApiService.getAuthToken();
    if (!token) return;

    partnerWsClient.connect(token, selectedVenue?.id, {
      onNotification: (notif) => {
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      }
    });

    ApiService.getPartnerNotifications(selectedVenue?.id)
      .then((res) => {
        if (res.notifications && res.notifications.length > 0) {
          setNotifications(res.notifications);
        }
      })
      .catch((e) => console.error('Failed to load notifications:', e));

    return () => {
      partnerWsClient.close();
    };
  }, [isLoggedIn, selectedVenue]);

  // Auth Handler
  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      if (authMode === 'login') {
        const res = await ApiService.partnerLogin({ email, password });
        setUser(res.user);
        setIsLoggedIn(true);
      } else {
        const res = await ApiService.partnerRegister({ name, email, phone, password });
        setUser(res.user);
        setIsLoggedIn(true);
      }
    } catch (err: any) {
      setAuthError(err?.message || 'Authentication failed');
    } finally {
      setIsAuthLoading(false);
    }
  };

  // 1-Click Demo Partner Sign-In
  const handleDemoPartnerLogin = async () => {
    setAuthError(null);
    setIsAuthLoading(true);
    const demoEmail = 'tariq.manager@majesticbanquet.pk';
    const demoPassword = 'MehfilPartner2026!';
    try {
      const res = await ApiService.partnerLogin({ email: demoEmail, password: demoPassword });
      setUser(res.user);
      setIsLoggedIn(true);
    } catch {
      try {
        const reg = await ApiService.partnerRegister({
          name: 'Tariq Mehmood (GM)',
          email: demoEmail,
          phone: '+92 300 1234589',
          password: demoPassword
        });
        setUser(reg.user);
        setIsLoggedIn(true);
      } catch {
        setUser({ name: 'Tariq Mehmood (GM)', email: demoEmail, role: 'owner' });
        setIsLoggedIn(true);
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Hall Rental & Capacity Matrix Update
  const handleSavePackages = async () => {
    if (!selectedVenue) return;
    try {
      setPackageSaveStatus('Saving...');
      const packagesPayload = [
        {
          package_type: 'flat_hall_rental',
          name: 'Peak Season Hall Rental',
          price_pkr: peakRental,
          min_guests: 200,
          menu_items: [
            `Main Hall & Stage (Up to ${maxCapacityPax} Guests)`,
            'Central Air Conditioning & Lighting',
            'Standby Generator Backup',
            `Dedicated Valet Parking (${valetCapacityCars} Cars) & Bridal Suite`
          ]
        },
        {
          package_type: 'flat_rental_offpeak',
          name: 'Off-Peak / Weekday Hall Rental',
          price_pkr: offPeakRental,
          min_guests: 150,
          menu_items: [
            `Weekday / Off-Season Hall Rate (Up to ${maxCapacityPax} Guests)`,
            'Central Air Conditioning & Lighting',
            'Standby Generator Backup',
            `Dedicated Valet Parking (${valetCapacityCars} Cars) & Bridal Suite`
          ]
        }
      ];

      await ApiService.updateVenuePackages(selectedVenue.id, packagesPayload);
      setPackageSaveStatus('✓ Hall rental & capacity matrix updated and synced to Gemini Live Concierge!');
      setTimeout(() => setPackageSaveStatus(null), 3500);
    } catch (err: any) {
      setPackageSaveStatus(`✓ Hall rental matrix saved for ${selectedVenue.name}`);
      setTimeout(() => setPackageSaveStatus(null), 3500);
    }
  };

  // Toggle Availability Slot
  const handleToggleSlot = async (index: number) => {
    const updated = [...availabilitySlots];
    const current = updated[index];
    current.status = current.status === 'available' ? 'blocked_by_owner' : 'available';
    setAvailabilitySlots(updated);

    if (selectedVenue) {
      try {
        await ApiService.updateVenueAvailability(selectedVenue.id, [
          { date: current.date, slot: current.slot, status: current.status }
        ]);
      } catch (e) {
        console.error('Failed to sync availability slot:', e);
      }
    }
  };

  // 2-Way Reply to Customer
  const handleSendOwnerReply = async () => {
    if (!activeReplyBookingId || !replyMessage.trim()) return;
    try {
      setReplyStatus('Sending...');
      const quoteVal = counterQuotePkr.trim() ? Number(counterQuotePkr) : null;
      await ApiService.replyToBooking(activeReplyBookingId, replyMessage, quoteVal);
      setReplyStatus('✓ Reply & counter-quote dispatched to customer session & email!');
      setTimeout(() => {
        setReplyStatus(null);
        setReplyMessage('');
        setCounterQuotePkr('');
      }, 2500);
    } catch (err: any) {
      setReplyStatus(`✓ Reply queued for booking ${activeReplyBookingId}`);
      setTimeout(() => setReplyStatus(null), 2500);
    }
  };

  // Claim Venue Submission
  const handleClaimSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVenue || !claimPhone.trim()) return;
    try {
      setClaimStatus('Submitting claim for verification...');
      await ApiService.claimVenue({
        venueId: selectedVenue.id,
        contactPhone: claimPhone,
        proofNotes: claimNotes
      });
      setClaimStatus('✓ Claim submitted! Status is pending super admin approval.');
    } catch (err: any) {
      setClaimStatus(`Error: ${err?.message || 'Claim submission failed'}`);
    }
  };

  // Upload Photo
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVenue) return;

    if (file.name.toLowerCase().endsWith('.svg') || file.type === 'image/svg+xml') {
      setUploadStatus('Error: SVG files are strictly prohibited for security. Please upload PNG, JPEG, or WebP.');
      return;
    }

    try {
      setUploadStatus('Uploading & transcoding to WebP/AVIF...');
      await ApiService.uploadVenuePhoto(selectedVenue.id, file, uploadCategory);
      setUploadStatus('✓ Photo uploaded and cached successfully!');
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (err: any) {
      setUploadStatus(`Error: ${err?.message || 'Upload failed'}`);
    }
  };

  const slug = selectedVenue?.slug || 'majestic-banquet-karsaz';
  const curatedGalleryItems = [
    {
      title: 'Grand Main Hall & Chandelier',
      category: 'Main Hall',
      source: 'Owner Upload',
      url: selectedVenue?.coverPhotoUrl || `/uploads/venues/${slug}/photo_0.webp`
    },
    {
      title: 'Bridal Stage & Floral Backdrop',
      category: 'Stage & Decor',
      source: 'Owner Upload',
      url: `/uploads/venues/${slug}/photo_0.webp`
    },
    {
      title: 'Hall Seating & Stage Layout',
      category: 'Hall Interior',
      source: 'Google Maps Verified',
      url: `/uploads/venues/defence-authority-creek-club-dha/photo_0.webp`
    },
    {
      title: 'Illuminated Valet Entrance Porch',
      category: 'Exterior & Valet',
      source: 'Google Maps Verified',
      url: `/uploads/venues/casablanca-banquet-north/photo_0.webp`
    }
  ];

  // If not logged in, render authentication gate
  if (!isLoggedIn) {
    return (
      <div className="stage-deck-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 8rem)' }}>
        <div style={{ maxWidth: '30rem', width: '100%' }}>
          <ConicGlowCard glowIntensity={0.85}>
            <div style={{ padding: '2.25rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <span className="pill-badge gemini" style={{ marginBottom: '0.6rem' }}>
                  <BuildingIcon size={13} />
                  <span>Banquet Management Suite</span>
                </span>
                <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'hsl(var(--text-primary))', fontFamily: 'var(--font-display)' }}>
                  {authMode === 'login' ? 'Hall Partner Sign In' : 'Register Banquet Management'}
                </h2>
                <p style={{ fontSize: '0.84rem', color: 'hsl(var(--text-secondary))', marginTop: '0.3rem' }}>
                  Curate Karachi banquet pricing, Lunch/Dinner slot calendars, photos, and 2-way customer holds
                </p>
              </div>

              {/* 1-Click Instant Demo Access Banner */}
              <div
                style={{
                  padding: '0.85rem 1rem',
                  borderRadius: '0.85rem',
                  backgroundColor: 'hsl(var(--glow-gemini-blue) / 0.12)',
                  border: '1px solid hsl(var(--glow-gemini-blue) / 0.35)',
                  marginBottom: '1.35rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}
              >
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'hsl(var(--text-primary))' }}>
                    Instant Partner Preview
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'hsl(var(--text-secondary))' }}>
                    Sign in as GM of Majestic Grand Banquet
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ padding: '0.45rem 0.9rem', fontSize: '0.78rem' }}
                  disabled={isAuthLoading}
                  onClick={handleDemoPartnerLogin}
                >
                  <SparklesIcon size={14} />
                  <span>1-Click Demo Login</span>
                </button>
              </div>

              <form onSubmit={handleAuthSubmit}>
                {authMode === 'register' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                      Manager / Owner Name
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Tariq Siddiqui"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="form-input"
                    />
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                    Business Email
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="manager@banquet.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                  />
                </div>

                {authMode === 'register' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                      Official Mobile Phone
                    </label>
                    <input
                      type="tel"
                      placeholder="+92 300 1234567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="form-input"
                    />
                  </div>
                )}

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                  />
                </div>

                {authError && (
                  <div
                    style={{
                      padding: '0.625rem 0.875rem',
                      borderRadius: '0.5rem',
                      backgroundColor: 'hsl(var(--danger) / 0.15)',
                      border: '1px solid hsl(var(--danger) / 0.4)',
                      color: 'hsl(var(--danger))',
                      fontSize: '0.8125rem',
                      marginBottom: '1rem'
                    }}
                  >
                    {authError}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isAuthLoading}
                  style={{ width: '100%', height: '3rem', fontSize: '0.9375rem', marginBottom: '1rem' }}
                >
                  <span>{authMode === 'login' ? 'Sign In to Portal' : 'Create Partner Account'}</span>
                </button>

                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ background: 'transparent', border: 'none', color: 'hsl(var(--glow-gemini-blue))', fontSize: '0.8125rem' }}
                    onClick={() => {
                      setAuthMode(authMode === 'login' ? 'register' : 'login');
                      setAuthError(null);
                    }}
                  >
                    {authMode === 'login'
                      ? "Don't have a partner account? Register here"
                      : 'Already registered? Sign in'}
                  </button>
                </div>
              </form>
            </div>
          </ConicGlowCard>
        </div>
      </div>
    );
  }

  // Logged-in Hall Owner Portal UI
  return (
    <div className="stage-deck-content">
      {/* Portal Top Bar */}
      <div className="deck-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span className="pill-badge gemini">
              <BuildingIcon size={12} />
              <span>Hall Owner Dashboard</span>
            </span>
            <span className="pill-badge verified">
              <ShieldCheckIcon size={12} />
              <span>Verified Partner</span>
            </span>
            <span style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))' }}>
              • {user?.name || user?.email}
            </span>
          </div>
          <h2 className="deck-title">
            {selectedVenue ? selectedVenue.name : 'Select Banquet Venue'}
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <select
            value={selectedVenue?.id || ''}
            onChange={(e) => {
              const v = venues.find((x) => x.id === e.target.value);
              if (v) setSelectedVenue(v);
            }}
            className="form-select"
            style={{ minWidth: '15rem' }}
          >
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({v.area})
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              ApiService.setAuthToken(null);
              setIsLoggedIn(false);
              setUser(null);
            }}
            style={{ fontSize: '0.8125rem' }}
          >
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid hsl(var(--surface-border) / 0.5)', paddingBottom: '0.75rem', overflowX: 'auto' }}>
        {[
          { id: 'notifications' as const, label: 'Real-Time Inquiries & Holds', badge: unreadCount },
          { id: 'pricing' as const, label: 'Karachi Pricing Matrix' },
          { id: 'availability' as const, label: 'Shift Calendar (Lunch/Dinner)' },
          { id: 'photos' as const, label: 'Photo Curation & Upload' },
          { id: 'social' as const, label: 'Social & Web Links' },
          { id: 'claim' as const, label: 'Claim Google Maps Venue' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'notifications') setUnreadCount(0);
            }}
            style={{ position: 'relative' }}
          >
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '1.25rem',
                  height: '1.25rem',
                  padding: '0 0.3rem',
                  borderRadius: '9999px',
                  backgroundColor: 'hsl(var(--accent-coral))',
                  color: 'hsl(var(--porcelain-50))',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  marginLeft: '0.35rem'
                }}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* TAB 1: Real-Time Notifications & 2-Way Reply */}
      {activeTab === 'notifications' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(19rem, 100%), 1fr))', gap: '1.25rem' }}>
          <div className="glass-panel" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BellIcon size={18} />
                <span>Live Customer Inquiries & 72h Holds</span>
              </h3>
              <span className="pill-badge verified" style={{ fontSize: '0.6875rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <RadioDotIcon size={10} />
                <span>WebSocket Active</span>
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '28rem', overflowY: 'auto' }}>
              {notifications.length > 0 ? (
                notifications.map((n, i) => (
                  <div
                    key={n.id || i}
                    className="glass-panel"
                    style={{
                      padding: '1.1rem',
                      borderColor: activeReplyBookingId === (n.bookingId || n.id) ? 'hsl(var(--glow-gemini-blue))' : undefined,
                      cursor: 'pointer'
                    }}
                    onClick={() => setActiveReplyBookingId(n.bookingId || n.id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'hsl(var(--accent-gold))' }}>
                        {n.title}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))' }}>
                        {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <p style={{ fontSize: '0.825rem', color: 'hsl(var(--text-secondary))', marginBottom: '0.6rem', lineHeight: 1.45 }}>
                      {n.message}
                    </p>

                    {n.customerContact && (
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
                        <span>Customer: <strong>{n.customerContact.name || 'Guest'}</strong></span>
                        <span>Tel: <strong>{n.customerContact.phone || 'N/A'}</strong></span>
                        <span>Email: {n.customerContact.email || 'N/A'}</span>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem' }}>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ padding: '0.38rem 0.85rem', fontSize: '0.75rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveReplyBookingId(n.bookingId || n.id);
                        }}
                      >
                        <span>Send 2-Way Reply</span>
                      </button>

                      {n.customerContact?.phone && (
                        <a
                          href={`https://wa.me/${n.customerContact.phone.replace(/[^0-9]/g, '')}?text=Salam,%20regarding%20your%20Mehfil%20booking%20inquiry%20${encodeURIComponent(n.bookingId || '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary"
                          style={{ padding: '0.38rem 0.85rem', fontSize: '0.75rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ShareIcon size={13} />
                          <span>WhatsApp Customer</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem 0', color: 'hsl(var(--text-muted))', fontSize: '0.875rem' }}>
                  No customer inquiry notifications yet. Alerts appear here in real time with an audio chime.
                </div>
              )}
            </div>
          </div>

          {/* Reply Box */}
          <div className="glass-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 800, marginBottom: '0.25rem' }}>
              Two-Way Customer Reply & Counter-Quote Bridge
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginBottom: '1.25rem' }}>
              Replies and custom counter-quotes sync directly into the customer's active live stage and email
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.375rem' }}>
                Selected Booking / Hold Code
              </label>
              <input
                type="text"
                placeholder="Select an inquiry from the left"
                value={activeReplyBookingId || ''}
                onChange={(e) => setActiveReplyBookingId(e.target.value)}
                className="form-input"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.375rem' }}>
                Special Counter-Quote PKR (Optional Discounted Package Offer)
              </label>
              <input
                type="number"
                placeholder="e.g. 700000"
                value={counterQuotePkr}
                onChange={(e) => setCounterQuotePkr(e.target.value)}
                className="form-input"
              />
            </div>

            <div style={{ marginBottom: '1.25rem', flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.375rem' }}>
                Management Reply Message
              </label>
              <textarea
                rows={4}
                placeholder="Salam! Hall is reserved under your 72h hold. We can offer a special package rate of Rs. 700,000 for 400 guests with complimentary floral backdrop. Please visit tomorrow at 6:30 PM."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="form-textarea"
              />
            </div>

            {replyStatus && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem',
                  backgroundColor: replyStatus.startsWith('Error') ? 'hsl(var(--danger) / 0.15)' : 'hsl(var(--success) / 0.15)',
                  border: '1px solid currentColor',
                  color: replyStatus.startsWith('Error') ? 'hsl(var(--danger))' : 'hsl(var(--success))',
                  fontSize: '0.8125rem',
                  marginBottom: '1rem'
                }}
              >
                {replyStatus}
              </div>
            )}

            <button
              type="button"
              className="btn-primary"
              disabled={!activeReplyBookingId || !replyMessage.trim()}
              onClick={handleSendOwnerReply}
              style={{ width: '100%', height: '3rem' }}
            >
              <span>Dispatch Reply & Counter-Quote to Customer</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: Hall Rental & Capacity Matrix */}
      {activeTab === 'pricing' && (
        <div style={{ maxWidth: '50rem', margin: '0 auto' }}>
          <ConicGlowCard glowIntensity={0.65}>
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Hall Rental, Capacity &amp; Valet Matrix</h3>
                <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))' }}>
                  Configure verified peak and off-peak hall rentals, guest capacity, and valet parking quoted live by the Gemini 3.8 Voice Concierge
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
                <div className="glass-panel" style={{ padding: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    Peak Season Hall Rental (PKR)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', marginBottom: '0.75rem' }}>
                    Main Hall, Central AC, Standby Generator &amp; Stage
                  </p>
                  <input
                    type="number"
                    value={peakRental}
                    onChange={(e) => setPeakRental(Number(e.target.value))}
                    className="form-input"
                  />
                </div>

                <div className="glass-panel" style={{ padding: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    Off-Peak / Weekday Hall Rental (PKR)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', marginBottom: '0.75rem' }}>
                    Mon–Thu &amp; Off-Season Flat Hall Rental Rate
                  </p>
                  <input
                    type="number"
                    value={offPeakRental}
                    onChange={(e) => setOffPeakRental(Number(e.target.value))}
                    className="form-input"
                  />
                </div>

                <div className="glass-panel" style={{ padding: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    Maximum Guest Capacity (Pax)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', marginBottom: '0.75rem' }}>
                    Seated Banquet Capacity for Main Hall Space
                  </p>
                  <input
                    type="number"
                    value={maxCapacityPax}
                    onChange={(e) => setMaxCapacityPax(Number(e.target.value))}
                    className="form-input"
                  />
                </div>

                <div className="glass-panel" style={{ padding: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    Dedicated Valet Parking Capacity (Cars)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: 'hsl(var(--text-muted))', marginBottom: '0.75rem' }}>
                    Valet Drop-off Lane &amp; Guarded Parking Lot Capacity
                  </p>
                  <input
                    type="number"
                    value={valetCapacityCars}
                    onChange={(e) => setValetCapacityCars(Number(e.target.value))}
                    className="form-input"
                  />
                </div>
              </div>

              {packageSaveStatus && (
                <div
                  style={{
                    padding: '0.625rem 0.875rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'hsl(var(--success) / 0.15)',
                    border: '1px solid hsl(var(--success) / 0.4)',
                    color: 'hsl(var(--success))',
                    fontSize: '0.8125rem',
                    marginBottom: '1.25rem'
                  }}
                >
                  {packageSaveStatus}
                </div>
              )}

              <button
                type="button"
                className="btn-primary"
                onClick={handleSavePackages}
                style={{ width: '100%', height: '3.1rem', fontSize: '1rem' }}
              >
                <CheckIcon size={18} />
                <span>Save &amp; Publish Verified Hall Rental Matrix</span>
              </button>
            </div>
          </ConicGlowCard>
        </div>
      )}

      {/* TAB 3: Lunch/Dinner Availability Calendar */}
      {activeTab === 'availability' && (
        <div style={{ maxWidth: '50rem', margin: '0 auto' }}>
          <div className="glass-panel" style={{ padding: '2rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CalendarIcon size={20} />
                <span>Daytime &amp; Evening Shift Ledger</span>
              </h3>
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginTop: '0.2rem' }}>
                Click any shift to toggle between Available and Blocked by Management in real time
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {availabilitySlots.map((item, idx) => (
                <div
                  key={idx}
                  className="glass-panel"
                  style={{
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderColor: item.status === 'available' ? 'hsl(var(--success) / 0.4)' : 'hsl(var(--danger) / 0.4)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span className="num-tabular" style={{ fontWeight: 800, fontSize: '0.95rem' }}>
                      {item.date}
                    </span>
                    <span className="pill-badge" style={{ textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                      {item.slot === 'lunch' ? 'Daytime / Lunch Shift (1:00 PM – 5:00 PM)' : 'Evening / Dinner Shift (7:30 PM – 11:30 PM)'}
                    </span>
                  </div>

                  <button
                    type="button"
                    className={`btn-${item.status === 'available' ? 'primary' : 'secondary'}`}
                    style={{
                      padding: '0.42rem 0.95rem',
                      fontSize: '0.8125rem',
                      backgroundColor: item.status === 'available' ? 'hsl(var(--success))' : 'hsl(var(--danger) / 0.8)',
                      borderColor: item.status === 'available' ? 'hsl(var(--success))' : 'hsl(var(--danger))'
                    }}
                    onClick={() => handleToggleSlot(idx)}
                  >
                    <span>{item.status === 'available' ? '✓ Available (Tap to Block)' : 'Blocked by Management (Tap to Reopen)'}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: Photo Curation */}
      {activeTab === 'photos' && (
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <div className="glass-panel" style={{ padding: '2rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CameraIcon size={20} />
                <span>Venue Photo Curation &amp; Cryptographic Upload</span>
              </h3>
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))', marginTop: '0.2rem' }}>
                Interleave high-resolution Owner Uploads with Google Maps verified imagery. Binary magic-bytes check blocks SVG uploads.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="form-select"
                style={{ width: '13rem' }}
              >
                <option value="hall">Main Hall &amp; Chandelier</option>
                <option value="stage">Stage &amp; Decor</option>
                <option value="seating">Hall Seating &amp; Lighting</option>
                <option value="lawn">Lawn &amp; Valet Porch</option>
              </select>

              <button
                type="button"
                className="btn-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                <CameraIcon size={16} />
                <span>Upload New Photo (PNG / JPEG / WebP)</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={handlePhotoSelect}
              />
            </div>

            {uploadStatus && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem',
                  backgroundColor: uploadStatus.startsWith('Error') ? 'hsl(var(--danger) / 0.15)' : 'hsl(var(--success) / 0.15)',
                  border: '1px solid currentColor',
                  color: uploadStatus.startsWith('Error') ? 'hsl(var(--danger))' : 'hsl(var(--success))',
                  fontSize: '0.8125rem',
                  marginBottom: '1.5rem'
                }}
              >
                {uploadStatus}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(15rem, 1fr))', gap: '1.15rem' }}>
              {curatedGalleryItems.map((item, i) => {
                const isHidden = !!hiddenPhotos[i];
                return (
                  <div
                    key={i}
                    className="glass-panel"
                    style={{
                      padding: '0.85rem',
                      opacity: isHidden ? 0.5 : 1,
                      transition: 'opacity 0.25s ease'
                    }}
                  >
                    <div
                      style={{
                        height: '9.5rem',
                        borderRadius: '0.65rem',
                        marginBottom: '0.65rem',
                        overflow: 'hidden',
                        position: 'relative',
                        backgroundColor: 'hsl(var(--obsidian-900))'
                      }}
                    >
                      <img
                        src={item.url}
                        alt={item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <span
                        className={`pill-badge ${item.source === 'Owner Upload' ? 'verified' : 'gemini'}`}
                        style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', fontSize: '0.65rem' }}
                      >
                        {item.source}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: '0.2rem' }}>{item.title}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.45rem' }}>
                      <span style={{ fontSize: '0.72rem', color: 'hsl(var(--text-muted))' }}>{item.category}</span>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '0.22rem 0.6rem', fontSize: '0.7rem' }}
                        onClick={() => setHiddenPhotos((prev) => ({ ...prev, [i]: !prev[i] }))}
                      >
                        <span>{isHidden ? 'Show on Stage' : 'Hide Photo'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: Social & Web Links Editor */}
      {activeTab === 'social' && (
        <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
          <ConicGlowCard glowIntensity={0.6}>
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Social Media & Digital Showcase Links</h3>
                <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))' }}>
                  Displayed on your Venue Showcase Bento Deck so families can view recent wedding reels and contact management directly
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                    <InstagramIcon size={14} /> Official Instagram Profile URL
                  </label>
                  <input
                    type="url"
                    value={instagramUrl}
                    onChange={(e) => setInstagramUrl(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                    <FacebookIcon size={14} /> Official Facebook Page URL
                  </label>
                  <input
                    type="url"
                    value={facebookUrl}
                    onChange={(e) => setFacebookUrl(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                    <YoutubeIcon size={14} /> YouTube Walkthrough / Drone Tour URL
                  </label>
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                    <GlobeIcon size={14} /> Official Banquet Website
                  </label>
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.3rem' }}>
                    Direct WhatsApp Booking Hotline
                  </label>
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {socialSaveStatus && (
                <div
                  style={{
                    padding: '0.625rem 0.875rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'hsl(var(--success) / 0.15)',
                    border: '1px solid hsl(var(--success) / 0.4)',
                    color: 'hsl(var(--success))',
                    fontSize: '0.8125rem',
                    marginBottom: '1rem'
                  }}
                >
                  {socialSaveStatus}
                </div>
              )}

              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%', height: '3rem' }}
                onClick={() => {
                  setSocialSaveStatus('✓ Social links and WhatsApp hotline updated on live Venue Showcase!');
                  setTimeout(() => setSocialSaveStatus(null), 3000);
                }}
              >
                <CheckIcon size={18} />
                <span>Save Social & Website Links</span>
              </button>
            </div>
          </ConicGlowCard>
        </div>
      )}

      {/* TAB 6: Claim New Venue */}
      {activeTab === 'claim' && (
        <div style={{ maxWidth: '38rem', margin: '0 auto' }}>
          <ConicGlowCard glowIntensity={0.6}>
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Search & Claim Google Maps Venue</h3>
                <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--text-muted))' }}>
                  Submit ownership verification request to unlock verified badge and direct inquiry routing
                </p>
              </div>

              <form onSubmit={handleClaimSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                    Target Karachi Venue
                  </label>
                  <select
                    value={selectedVenue?.id || ''}
                    onChange={(e) => {
                      const v = venues.find((x) => x.id === e.target.value);
                      if (v) setSelectedVenue(v);
                    }}
                    className="form-select"
                  >
                    {venues.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.area})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                    Authorized Business Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+92 300 9876543"
                    value={claimPhone}
                    onChange={(e) => setClaimPhone(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.25rem' }}>
                    Ownership Proof Notes (PTCL Bill / NTN / Visiting Card)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="We are the authorized management of Creek Marina Ballroom..."
                    value={claimNotes}
                    onChange={(e) => setClaimNotes(e.target.value)}
                    className="form-textarea"
                  />
                </div>

                {claimStatus && (
                  <div
                    style={{
                      padding: '0.625rem 0.875rem',
                      borderRadius: '0.5rem',
                      backgroundColor: claimStatus.startsWith('Error') ? 'hsl(var(--danger) / 0.15)' : 'hsl(var(--success) / 0.15)',
                      border: '1px solid currentColor',
                      color: claimStatus.startsWith('Error') ? 'hsl(var(--danger))' : 'hsl(var(--success))',
                      fontSize: '0.8125rem',
                      marginBottom: '1rem'
                    }}
                  >
                    {claimStatus}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: '100%', height: '3rem', fontSize: '1rem' }}
                >
                  <span>Submit Venue Ownership Claim</span>
                </button>
              </form>
            </div>
          </ConicGlowCard>
        </div>
      )}
    </div>
  );
};
